import {
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { athleteDisplayName, AthleteTeamProfile } from '@strength/domain';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import { AthleteAvatar } from '../../../../components/icons/athlete-avatar';
import { Input } from '../../../../components/input/input';
import { InputLabel } from '../../../../components/input/input-label';
import { SearchInput } from '../../../../components/input/search-input';
import { useToast } from '../../../../hooks/useToast';
import { useTrackMutations } from '../../hooks/useTrackMutations';
import { TrackPreview } from '../types';

export function getTrackAthletes(
  track: Pick<TrackPreview, 'athletes'>,
  athletes: readonly AthleteTeamProfile[],
): AthleteTeamProfile[] {
  const profileIds = new Set(track.athletes.map((a) => a.profileId));
  return athletes.filter((athlete) => profileIds.has(athlete.id));
}

export function filterAthletesBySearchTerm<
  T extends { givenName: string; familyName: string },
>(athletes: T[], searchTerm: string): T[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return athletes;
  return athletes.filter((athlete) =>
    athleteDisplayName(athlete).toLowerCase().includes(term),
  );
}

export function ViewArchivedTrackDrawer({
  track,
  athletes,
  onClose,
  refetchTracks,
  isParentArchived = false,
}: {
  track: TrackPreview;
  athletes: readonly AthleteTeamProfile[];
  onClose: () => void;
  refetchTracks: () => void;
  // A sub-track under an archived parent cannot be unarchived on its own — the
  // server rejects it, since an archived parent must not have an active child.
  // Unarchiving the parent is the route back to active.
  isParentArchived?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  const { unarchiveTrack } = useTrackMutations();
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const isSubTrack = track.parentTrackId != null;

  const handleUnarchive = () => {
    setIsUnarchiving(true);
    unarchiveTrack({ trackId: track.id, teamId: track.teamId })
      .then(() => {
        refetchTracks();
        toast({
          description: isSubTrack
            ? t('subTrackUnarchived')
            : t('trackUnarchived'),
          severity: 'success',
          title: t('success'),
        });
        onClose();
      })
      .catch(() => {
        toast({
          title: isSubTrack
            ? t('errorUnarchivingSubTrack')
            : t('errorUnarchivingTrack'),
          severity: 'error',
        });
      })
      .finally(() => {
        setIsUnarchiving(false);
      });
  };

  const trackAthletes = useMemo(
    () => getTrackAthletes(track, athletes),
    [track, athletes],
  );
  const filteredAthletes = useMemo(
    () => filterAthletesBySearchTerm(trackAthletes, debouncedSearchTerm),
    [trackAthletes, debouncedSearchTerm],
  );

  return (
    <Drawer open onClose={onClose} anchor="right">
      <DrawerHeader title={t('viewArchivedTrack')} onClose={onClose} />
      <div className="flex flex-col flex-1 min-h-0 px-8 py-4 gap-2">
        <div className="flex flex-col gap-1">
          <InputLabel label={t('name')} />
          <Input
            value={track.name}
            readOnly
            aria-label={t('name')}
            className="bg-muted text-muted-foreground"
          />
        </div>
        <SearchInput
          label={t('athletes')}
          placeholder={t('searchForAthlete')}
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            debouncedSetSearchTerm(value);
          }}
        />
        <div className="flex-1 min-h-0 overflow-y-auto border rounded">
          {filteredAthletes.length === 0 ? (
            <p className="text-sm text-gray-500 p-4">
              {debouncedSearchTerm
                ? t('noAthletesFoundMatching', {
                    searchTerm: debouncedSearchTerm,
                  })
                : t('noAthletes')}
            </p>
          ) : (
            <List
              dense
              sx={{
                padding: 0,
                '& .MuiListItem-root': {
                  mb: 1,
                  '&:last-child': { mb: 0 },
                },
              }}
            >
              {filteredAthletes.map((athlete) => (
                <ListItem key={athlete.id}>
                  <ListItemAvatar sx={{ minWidth: 0, paddingRight: '8px' }}>
                    <AthleteAvatar
                      athlete={athlete}
                      imgSrc={athlete.imageUrl ?? ''}
                      sx={{ width: 24, height: 24, fontSize: 12 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={athleteDisplayName(athlete)}
                    secondary={
                      athlete.attributes?.positions
                        ? athlete.attributes.positions.join(', ')
                        : undefined
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </div>
      </div>
      <DrawerFooter className="flex flex-row justify-end gap-2 w-full p-8">
        <Button onClick={onClose} variant="outlined">
          {t('cancel')}
        </Button>
        {!isParentArchived && (
          <Button onClick={handleUnarchive} disabled={isUnarchiving}>
            {isSubTrack ? t('unarchiveSubTrack') : t('unarchiveTrack')}
          </Button>
        )}
      </DrawerFooter>
    </Drawer>
  );
}
